import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Palette } from "lucide-react";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { type SwatchOption } from "./printOptions";

type Props = {
  label: string;
  value: string;
  options: SwatchOption[];
  onChange: (hex: string) => void;
  error?: string;
  onBlur?: () => void;
  variant?: "desktop" | "mobile";
};

export function ColorSwatchPicker({ label, value, options, onChange, error, onBlur, variant = "desktop" }: Props) {
  const { t } = useTranslation("settings");
  const id = useId();
  const errorId = `${id}-error`;
  const customColorActive = !options.some((option) => option.value.toLowerCase() === value.toLowerCase());
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={`${id}-hex`}>{label}</Label>
      <div role="group" aria-label={label} className="flex flex-wrap items-center gap-2">
        {options.map((option) => {
          const active = option.value.toLowerCase() === value.toLowerCase();
          const name = t(`printEditor.body.colors.${option.labelKey}`);
          return (
            <button
              key={option.value}
              type="button"
              title={name}
              aria-label={name}
              aria-pressed={active}
              onClick={() => onChange(option.value)}
              // Inline style: swatch color is data (the palette value itself).
              style={{ backgroundColor: option.value }}
              className={cn(
                variant === "mobile" ? "h-11 w-11" : "h-[34px] w-[34px]",
                "rounded-full border-[3px] border-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active && "ring-2 ring-primary",
              )}
            />
          );
        })}
        <label
          title={t("printEditor.body.customColor")}
          aria-label={t("printEditor.body.customColor")}
          className={cn(
            "relative inline-flex cursor-pointer items-center justify-center overflow-hidden rounded-full bg-[conic-gradient(hsl(var(--chart-1)),_hsl(var(--chart-2)),_hsl(var(--chart-3)),_hsl(var(--chart-4)),_hsl(var(--chart-5)),_hsl(var(--chart-1)))] p-[3px] ring-1 ring-border focus-within:ring-2 focus-within:ring-ring",
            variant === "mobile" ? "h-11 w-11" : "h-[34px] w-[34px]",
            customColorActive && "ring-2 ring-primary",
          )}
        >
          <span aria-hidden="true" className="pointer-events-none flex h-full w-full items-center justify-center rounded-full bg-card">
            <Palette className="h-[17px] w-[17px] text-foreground" />
          </span>
          <input
            type="color"
            aria-label={`${label}: ${t("printEditor.body.customColor")}`}
            value={/^#[0-9A-Fa-f]{6}$/.test(value) ? value : "#000000"}
            onChange={(event) => onChange(event.target.value.toUpperCase())}
            onBlur={onBlur}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </label>
        <Input
          id={`${id}-hex`}
          aria-label={t("printEditor.body.hex")}
          aria-describedby={error ? errorId : undefined}
          invalid={!!error}
          value={value}
          maxLength={7}
          onChange={(event) => {
            const raw = event.target.value.trim();
            onChange(raw && !raw.startsWith("#") ? `#${raw}` : raw);
          }}
          onBlur={onBlur}
          className="w-[104px] font-mono uppercase"
        />
      </div>
      {error ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
